import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const Analytics = () => {
  const [analytics, setAnalytics] = useState({
    salesCount: 0,
    totalRevenue: 0,
    revenueToday: 0,
    revenueByDay: [],
    revenueByCourse: {},
    revenueByCoupon: {},
    revenueBySubCategory: {},
  });
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await fetch(`https://pukfh1sc9a.execute-api.ap-south-1.amazonaws.com/prod/api/demotransaction/analytics?${new URLSearchParams(filters)}`);
        const data = await response.json();
        setAnalytics(data);
      } catch (error) {
        console.error("Failed to fetch analytics:", error);
      }
    };
    fetchAnalytics();
  }, [filters]);

  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Convert object data to chart-friendly format
  const courseData = Object.entries(analytics.revenueByCourse).map(([name, revenue]) => ({ name, revenue }));
  const couponData = Object.entries(analytics.revenueByCoupon)
    .map(([name, revenue]) => ({ name, revenue }))
    .sort((a, b) => b.revenue - a.revenue); // Sort by revenue descending
  const subCategoryData = Object.entries(analytics.revenueBySubCategory).map(([name, revenue]) => ({ name, revenue }));

  return (
    <div className="p-4">
      <div className="flex gap-4 mb-4">
        <Input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} />
        <Input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader><CardTitle>Total Revenue</CardTitle></CardHeader>
          <CardContent><p>₹{analytics.totalRevenue.toFixed(2)}</p></CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Revenue Today</CardTitle></CardHeader>
          <CardContent><p>₹{analytics.revenueToday.toFixed(2)}</p></CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Sales Count</CardTitle></CardHeader>
          <CardContent><p>{analytics.salesCount}</p></CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <h3 className="text-lg font-semibold mb-2">Revenue by Day</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics.revenueByDay}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="#8884d8" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-2">Revenue by Course</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={courseData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="revenue" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-2">Revenue by Coupon Code</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={couponData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="revenue" fill="#ffc107" />
            </BarChart>
          </ResponsiveContainer>
          {/* New Table for Sales by Coupon Code */}
          <div className="w-full md:w-1/2 mx-auto mt-4">
            <h3 className="text-lg font-semibold mb-2">Sales by Coupon Code (Ranked)</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Coupon Code</TableHead>
                  <TableHead>Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {couponData.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>₹{item.revenue.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-2">Revenue by Subcategory</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={subCategoryData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="revenue" fill="#ff7300" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Analytics;